/**
 * Which editor a fact gets, and how what the reader typed becomes what the API
 * expects.
 *
 * Deliberately dumb: this module shapes an input and parses a string. It does
 * not decide whether a value is *allowed* — that is a rule from the rulebase,
 * computed by the engine, and putting any of it here would be the API-layer
 * check logic the brief rules out. The server validates the type and answers
 * with its own sentence; the UI shows that sentence.
 */
import type { FactField, FactValue } from '@/api/types'

export type EditorKind = 'number' | 'boolean' | 'text' | 'list'

/**
 * Derived from the path prefix (the backend's own allowlist grouping) plus the
 * value that is actually there — `flags.baujahr` is an integer while
 * `flags.rueckbau` is a boolean, and only the value can tell them apart.
 */
export function editorKind(field: Pick<FactField, 'path' | 'value'>): EditorKind {
  if (Array.isArray(field.value)) return 'list'
  if (field.path === 'documents_present' || field.path === 'nachweise_erbracht') return 'list'
  if (typeof field.value === 'boolean') return 'boolean'
  if (field.path.startsWith('values.')) return 'number'
  if (field.path.startsWith('form.') || field.path.startsWith('flags.')) return 'number'
  return 'text'
}

/** Parse what the reader typed. An empty field means `null` — never `0`. */
export function parseInput(kind: EditorKind, raw: string): FactValue {
  const text = raw.trim()
  if (kind === 'list') {
    return text
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
  }
  if (text === '') return null
  if (kind === 'boolean') return text === 'true'
  if (kind === 'number') {
    // Swiss input habits: 1'234.5 and 1234,5 mean the same number. Anything
    // that still is not a number goes to the server AS TYPED, so the refusal
    // the reader sees is the server's own — not a guess made in the browser.
    const normalised = text.replace(/['’\s]/g, '').replace(',', '.')
    const parsed = Number(normalised)
    return Number.isFinite(parsed) ? parsed : text
  }
  return text
}

/** The current value as an editable string. */
export function toInputValue(kind: EditorKind, value: FactValue): string {
  if (value === null || value === undefined) return ''
  if (kind === 'list') return Array.isArray(value) ? value.join(', ') : String(value)
  if (kind === 'boolean') return value ? 'true' : 'false'
  return String(value)
}
